import * as arrow from "apache-arrow";
import React from "react";
import { DatePivotParams } from "../entities/date_pivot_params";
import dayjs from "../utils/dayjs";
import {
  getPeriodLabel,
  getSupplyCellBackgroundColor,
  getWOSMeasureKey,
} from "../utils/supply_color_coding";
import { Box, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const ASC_DEFAULT_MEASURES = [
  "Starting weeks of supply",
  "Starting months of supply",
  "Starting days of supply",
  "Ending weeks of supply",
  "Ending months of supply",
  "Ending days of supply",
  "In stock rate",
];

// Measures that should show a total column
const MEASURES_WITH_TOTALS = [
  "Purchase Order To Place",
  "Forecasted Sales",
  "Purchase Order Arrivals",
  "Unfulfilled Demand",
  "Transfer Order To Place",
];

interface RowData {
  type: "group" | "detail";
  groupKey?: string; // segmentationKey for group rows
  measure?: string; // measure name for detail rows
  dateValues: Record<string, any>;
  segmentationRank: number;
  measureRank?: number;
  isExpanded?: boolean;
  parentSegmentation?: string;
  groupSize?: number; // Added for group rows
  // Export compatibility properties
  segmentation?: string;
  [key: string]: any; // Allow dynamic keys for direct column access
}

interface MatrixData {
  columns: {
    key: string;
    label?: string;
    width?: number;
    isExpandColumn?: boolean;
    sortable?: boolean;
    onSort?: (() => void) | undefined;
    sticky?: boolean;
    renderCell?: (row: RowData, rowIndex: number) => React.ReactNode;
  }[];
  getRow: (rowIndex: number) => RowData;
  rowCount: number;
  arrowTable: arrow.Table;
  columnTotals?: Record<string, any>;
  segmentationIndex?: Map<
    string,
    {
      segmentationKey: string;
      segmentValues: string[];
      segmentationRank: number;
      selectedMeasureData: Map<number, any>;
      allMeasures: Map<
        number,
        {
          measure: string;
          measureRank: number;
          dateValues: Map<number, any>;
        }
      >;
    }
  >;
}

interface SegmentationInfo {
  segmentationKey: string;
  segmentValues: string[];
  segmentationRank: number;
  measureCount: number;
  startRowIndex: number;
}

export const createInventoryHealthForHubsMatrix = (args: {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
  viewParams: DatePivotParams;
  expandedGroups: Set<string>;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  setExpandedGroups: React.Dispatch<React.SetStateAction<Set<string>>>;
  theme: any;
}): MatrixData => {
  const {
    arrowTable,
    columns,
    viewParams,
    expandedGroups,
    sortConfig,
    setSortConfig,
    setExpandedGroups,
    theme,
  } = args;

  if (
    !arrowTable ||
    !columns ||
    !viewParams?.segments ||
    !viewParams?.dateHierarchy ||
    !viewParams?.measure
  ) {
    // Return empty table data instead of null
    return {
      columns: [],
      getRow: () => ({ type: "detail", dateValues: {}, segmentationRank: 0 }),
      rowCount: 0,
      arrowTable: arrow.tableFromArrays({}),
      columnTotals: undefined,
    };
  }

  // Get unique dates efficiently using Arrow vectors directly
  const dateField = viewParams.dateHierarchy;
  const dateVector = arrowTable.getChild(dateField);
  if (!dateVector) {
    // Return empty table data instead of null
    return {
      columns: [],
      getRow: () => ({ type: "detail", dateValues: {}, segmentationRank: 0 }),
      rowCount: 0,
      arrowTable: arrow.tableFromArrays({}),
      columnTotals: undefined,
    };
  }

  const dateSet = new Set<number>();
  for (let i = 0; i < dateVector.length; i++) {
    const dateValue = dateVector.get(i);
    if (dateValue != null) {
      dateSet.add(dayjs.utc(dateValue).valueOf());
    }
  }
  const allDates = Array.from(dateSet)
    .sort((a, b) => a - b)
    .map((ts) => dayjs.utc(ts));

  // Safety check: ensure we have valid dates
  if (allDates.length === 0) {
    console.warn("[InventoryHealthForHubsViewerComponent] No valid dates found");
    // Return empty table data instead of null
    return {
      columns: [],
      getRow: () => ({ type: "detail", dateValues: {}, segmentationRank: 0 }),
      rowCount: 0,
      arrowTable: arrow.tableFromArrays({}),
      columnTotals: undefined,
    };
  }
  // If no explicit sort config is provided, create default sort for all dates
  if (sortConfig.length === 0) {
    // Determine default direction based on measure type
    const defaultDirection: "asc" | "desc" = ASC_DEFAULT_MEASURES.some(
      (measure) => viewParams.measure?.toLowerCase() === measure.toLowerCase()
    )
      ? "asc" // Lower values are better for these measures
      : "desc"; // Higher values are better for other measures

    const initialSort = allDates.map((date, index) => ({
      key: `${date.valueOf()}`,
      direction: defaultDirection,
      index,
    }));

    setSortConfig(initialSort);

    return {
      columns: [],
      getRow: () => ({ type: "detail", dateValues: {}, segmentationRank: 0 }),
      rowCount: 0,
      arrowTable: arrow.tableFromArrays({}),
      columnTotals: undefined,
    };
  }

  // Get all necessary vectors once
  const segmentVectors = viewParams.segments.map((segment) =>
    arrowTable.getChild(segment)
  );
  const measureVector = arrowTable.getChild("measure");
  const valueVector = arrowTable.getChild("value");
  const segmentationRankVector =
    arrowTable.getChild("segmentation_rank") ||
    arrowTable.getChild("segmentationRank");
  const measureRankVector =
    arrowTable.getChild("measure_rank") || arrowTable.getChild("metric_rank");

  if (!measureVector || !valueVector || segmentVectors.some((v) => !v)) {
    // Return empty table data instead of null
    return {
      columns: [],
      getRow: () => ({ type: "detail", dateValues: {}, segmentationRank: 0 }),
      rowCount: 0,
      arrowTable: arrow.tableFromArrays({}),
      columnTotals: undefined,
    };
  }

  // Build segmentation index (keep Arrow data, just organize metadata)
  const segmentationIndex = new Map<
    string,
    {
      segmentationKey: string;
      segmentValues: string[];
      segmentationRank: number;
      selectedMeasureData: Map<number, any>; // dateValue -> value for selected measure
      allMeasures: Map<
        number,
        {
          measure: string;
          measureRank: number;
          dateValues: Map<number, any>; // dateValue -> value
        }
      >;
    }
  >();

  // Single pass through Arrow data - build index without converting to arrays
  for (let i = 0; i < arrowTable.numRows; i++) {
    const segmentValues = segmentVectors.map((vector) =>
      String(vector!.get(i) || "")
    );
    const segmentationKey = segmentValues.join(" | ");
    const measure = String(measureVector.get(i) || "");
    const dateValue = dayjs.utc(dateVector.get(i)).valueOf();
    const value = valueVector.get(i);
    const segmentationRank = Number(segmentationRankVector?.get(i) || 0);
    const measureRank = Number(measureRankVector?.get(i) || 0);

    // Initialize segmentation if not exists
    if (!segmentationIndex.has(segmentationKey)) {
      segmentationIndex.set(segmentationKey, {
        segmentationKey,
        segmentValues,
        segmentationRank,
        selectedMeasureData: new Map(),
        allMeasures: new Map(),
      });
    }

    const segData = segmentationIndex.get(segmentationKey)!;

    // Store data for selected measure
    if (measure.toLowerCase() === viewParams.measure.toLowerCase()) {
      segData.selectedMeasureData.set(dateValue, value);
    }

    // Store all measures (keyed by measureRank to allow duplicate measure names)
    if (!segData.allMeasures.has(measureRank)) {
      segData.allMeasures.set(measureRank, {
        measure,
        measureRank,
        dateValues: new Map(),
      });
    }
    segData.allMeasures.get(measureRank)!.dateValues.set(dateValue, value);
  }

  // Check if we should show totals column
  const shouldShowTotals = MEASURES_WITH_TOTALS.some(
    (measure) => viewParams.measure?.toLowerCase() === measure.toLowerCase()
  );

  // Calculate totals for each segmentation (for selected measure)
  const segmentationTotals = new Map<string, number>();
  if (shouldShowTotals) {
    segmentationIndex.forEach((segData, key) => {
      let total = 0;
      segData.selectedMeasureData.forEach((value) => {
        const numValue =
          typeof value === "number"
            ? value
            : typeof value === "string"
              ? parseFloat(value.replace(/,/g, "")) || 0
              : Number(value) || 0;
        total += numValue;
      });
      segmentationTotals.set(key, total);
    });
  }

  // Calculate totals for each measure in each segmentation (for detail rows)
  // Keyed by measureRank to match allMeasures
  const measureTotals = new Map<string, Map<number, number>>();
  if (shouldShowTotals) {
    segmentationIndex.forEach((segData, segKey) => {
      const measureTotalMap = new Map<number, number>();
      segData.allMeasures.forEach((measureData, measureRank) => {
        let total = 0;
        measureData.dateValues.forEach((value) => {
          const numValue =
            typeof value === "number"
              ? value
              : typeof value === "string"
                ? parseFloat(value.replace(/,/g, "")) || 0
                : Number(value) || 0;
          total += numValue;
        });
        measureTotalMap.set(measureRank, total);
      });
      measureTotals.set(segKey, measureTotalMap);
    });
  }

  const sortedSegmentations = Array.from(segmentationIndex.values()).sort(
    (a, b) => {
      let comparison = 0;

      // Check if we're sorting by TOTAL column
      const totalSort = sortConfig.find((s) => s.key === "TOTAL");
      if (totalSort) {
        const aTotal = segmentationTotals.get(a.segmentationKey) ?? 0;
        const bTotal = segmentationTotals.get(b.segmentationKey) ?? 0;
        comparison = aTotal - bTotal;
        if (comparison !== 0) {
          return totalSort.direction === "desc" ? -comparison : comparison;
        }
        // If totals are equal, maintain original order
        return 0;
      }

      // Apply multi-column date sorting in order of precedence
      for (const sort of sortConfig) {
        // Check if this is a date column by trying to parse the key as a timestamp
        const dateValue = parseInt(sort.key);
        if (!isNaN(dateValue) && dateValue > 1000000000000) {
          // Valid timestamp check
          const aValue = a.selectedMeasureData.get(dateValue) ?? 0;
          const bValue = b.selectedMeasureData.get(dateValue) ?? 0;

          // Convert to numbers, handling formatted strings with commas
          const aNum =
            typeof aValue === "number"
              ? aValue
              : typeof aValue === "string"
                ? parseFloat(aValue.replace(/,/g, "")) || 0
                : Number(aValue) || 0;
          const bNum =
            typeof bValue === "number"
              ? bValue
              : typeof bValue === "string"
                ? parseFloat(bValue.replace(/,/g, "")) || 0
                : Number(bValue) || 0;

          comparison = aNum - bNum;

          if (comparison !== 0) {
            return sort.direction === "desc" ? -comparison : comparison;
          }
        }
      }

      // If all date values are equal, maintain original order
      return 0;
    }
  );

  // Calculate row counts and positions without building full arrays
  const segmentationInfos: SegmentationInfo[] = [];
  let totalRowCount = 0;

  sortedSegmentations.forEach((segData) => {
    const isExpanded =
      expandedGroups.has("*") || expandedGroups.has(segData.segmentationKey);
    const measureCount = isExpanded ? segData.allMeasures.size : 0;

    segmentationInfos.push({
      segmentationKey: segData.segmentationKey,
      segmentValues: segData.segmentValues,
      segmentationRank: segData.segmentationRank,
      measureCount,
      startRowIndex: totalRowCount,
    });

    totalRowCount += 1 + measureCount; // 1 for segmentation row + measure rows
  });

  const handleDateColumnSort = (e?: React.MouseEvent<HTMLTableCellElement>) => {
    if (!setSortConfig) return;

    // Get all date columns using the timestamp values directly
    const dateColumns = allDates.map((date) => `${date.valueOf()}`);

    // Check if we're currently sorting by TOTAL - if so, restore date sorting
    const isCurrentlySortingByTotal = sortConfig.some((s) => s.key === "TOTAL");

    // Check current state - if any date column is explicitly sorted asc, we're in asc mode
    const hasAscSort = sortConfig?.some(
      (s) => s.direction === "asc" && s.key !== "TOTAL"
    );
    const hasDescSort = sortConfig?.some(
      (s) => s.direction === "desc" && s.key !== "TOTAL"
    );

    // Determine the new direction based on current state and measure type
    let newDirection: "asc" | "desc";

    if (isCurrentlySortingByTotal || (!hasAscSort && !hasDescSort)) {
      // Currently sorting by TOTAL or no sort applied yet - determine what the initial direction should be
      const isSupplyMeasure = ASC_DEFAULT_MEASURES.some(
        (measure) => viewParams.measure?.toLowerCase() === measure.toLowerCase()
      );
      const initialDirection = isSupplyMeasure ? "asc" : "desc";
      // Toggle to the opposite of the initial direction
      newDirection = initialDirection === "asc" ? "desc" : "asc";
    } else if (hasAscSort) {
      // Currently ascending - toggle to descending
      newDirection = "desc";
    } else {
      // Currently descending - toggle to ascending
      newDirection = "asc";
    }

    // Create sort config for all date columns with earlier dates having higher precedence
    const newSortConfig = dateColumns.map((col, index) => ({
      key: col,
      direction: newDirection,
      index,
    }));

    setSortConfig(newSortConfig);
  };

  const handleTotalColumnSort = (
    e?: React.MouseEvent<HTMLTableCellElement>
  ) => {
    if (!setSortConfig) return;

    // Check current state for TOTAL column
    const totalSort = sortConfig.find((s) => s.key === "TOTAL");
    const currentDirection = totalSort?.direction;

    // Determine new direction
    let newDirection: "asc" | "desc";
    if (!currentDirection) {
      // No sort on TOTAL yet - default to desc
      newDirection = "desc";
    } else if (currentDirection === "desc") {
      // Currently descending - toggle to ascending
      newDirection = "asc";
    } else {
      // Currently ascending - toggle to descending
      newDirection = "desc";
    }

    // Remove all date sorts and only sort by TOTAL
    setSortConfig([
      {
        key: "TOTAL",
        direction: newDirection,
        index: 0,
      },
    ]);
  };

  // Create MUI-compatible columns with header content
  const muiColumns = [
    {
      key: "expand",
      label: "",
      width: 40,
      isExpandColumn: true,
      sortable: false,
      sticky: true,
      renderCell: (row: RowData, rowIndex: number) => {
        const isGroup = row.type === "group";
        return isGroup
          ? React.createElement(Box, {
              sx: {
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 0,
              },
              children: React.createElement(IconButton, {
                size: "small",
                onClick: () =>
                  setExpandedGroups((prev) => {
                    const newSet = new Set(prev);
                    if (newSet.has(row.groupKey!)) {
                      newSet.delete(row.groupKey!);
                    } else {
                      newSet.add(row.groupKey!);
                    }
                    return newSet;
                  }),
                sx: {
                  p: 0,
                },
                children: row.isExpanded
                  ? React.createElement(ExpandLessIcon)
                  : React.createElement(ExpandMoreIcon),
              }),
            })
          : null;
      },
    },
    {
      key: "segmentation",
      label: "segmentation / measure",
      width: 200,
      sortable: false,
      sticky: true,
      renderCell: (row: RowData, rowIndex: number) => {
        const isGroup = row.type === "group";
        const isDetail = row.type === "detail";
        // Check if this is a PO arrivals breakdown row (open or simulated) or raw purchase need
        const isBreakdownRow =
          isDetail &&
          row.measure &&
          (row.measure.toLowerCase() === "open" ||
            row.measure.toLowerCase() === "simulated" ||
            row.measure.toLowerCase() === "raw purchase need");
        return React.createElement(Box, {
          sx: {
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            fontWeight: isGroup ? "bold" : "normal",
            backgroundColor: isDetail ? "action.hover" : undefined,
            paddingLeft: isBreakdownRow ? 4 : isDetail ? 2 : 1, // Extra indent for breakdown rows, normal indent for other detail rows
          },
          children: isGroup ? row.groupKey : row.measure,
        });
      },
    },
    ...allDates.map((date, index) => ({
      key: `${date.valueOf()}`,
      width: 120,
      sortable: true,
      onSort: handleDateColumnSort,
      headerType: new arrow.Timestamp(arrow.TimeUnit.MILLISECOND),
      renderCell: (row: RowData, rowIndex: number) => {
        const dateValue = parseInt(date.valueOf().toString());
        const value = row.dateValues[dateValue];
        const isGroup = row.type === "group";
        const isDetail = row.type === "detail";

        // Color coding for supply measures
        let cellColor = undefined;
        const numericString = String(value ?? "").replace(/,/g, "");

        // Check if this is a supply measure
        let isWOS = false;
        let currentMeasure = "";

        // Plain "<period>s of supply" is the inventory_wos_cora measure name
        // (no starting/ending prefix). Exact equality keeps the target row
        // ("target weeks of supply") uncolored.
        const plainWOSKey = `${getPeriodLabel(
          viewParams?.dateHierarchy || ""
        ).toLowerCase()} of supply`;

        if (isDetail && row.measure) {
          // For detail rows, check the measure name
          currentMeasure = row.measure;
          isWOS =
            currentMeasure.toLowerCase() ===
              getWOSMeasureKey(
                "starting",
                viewParams?.dateHierarchy || ""
              ).toLowerCase() ||
            currentMeasure.toLowerCase() ===
              getWOSMeasureKey(
                "ending",
                viewParams?.dateHierarchy || ""
              ).toLowerCase() ||
            currentMeasure.toLowerCase() === plainWOSKey;
        } else if (isGroup) {
          // For group rows (collapsed view), check if the selected measure is a supply measure
          currentMeasure = viewParams?.measure || "";
          const measureLower = currentMeasure.toLowerCase();

          isWOS =
            (measureLower.includes("starting") &&
              measureLower.includes("supply")) ||
            (measureLower.includes("ending") &&
              measureLower.includes("supply")) ||
            measureLower === plainWOSKey;
        }

        if (isWOS && numericString !== "" && !isNaN(Number(numericString))) {
          // Find target WOS from the original unfiltered data
          let targetWOS = null;
          const targetMeasureName = getWOSMeasureKey(
            "target",
            viewParams?.dateHierarchy || ""
          );

          // Get the segmentation data from the original unfiltered data
          const segData = segmentationIndex?.get(row.groupKey!);
          if (segData) {
            // Look for target measure in the allMeasures data
            for (const [, measureData] of segData.allMeasures) {
              if (
                measureData.measure.toLowerCase() === targetMeasureName.toLowerCase() ||
                (measureData.measure.toLowerCase().includes("target") &&
                  measureData.measure.toLowerCase().includes("supply"))
              ) {
                const targetValRaw = measureData.dateValues.get(dateValue);
                if (
                  targetValRaw !== undefined &&
                  targetValRaw !== null &&
                  targetValRaw !== ""
                ) {
                  const targetString =
                    typeof targetValRaw === "string"
                      ? targetValRaw.replace(/,/g, "")
                      : String(targetValRaw);
                  const parsedTarget = Number(targetString);
                  if (!isNaN(parsedTarget)) {
                    targetWOS = parsedTarget;
                  }
                }
                break;
              }
            }
          }

          const currentValue = Number(numericString);

          if (targetWOS !== null && !isNaN(targetWOS)) {
            cellColor = getSupplyCellBackgroundColor({
              value: currentValue,
              targetValue: targetWOS,
              theme,
            });
          }
        }

        return React.createElement(Box, {
          sx: {
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            backgroundColor:
              cellColor || (isDetail ? "action.hover" : undefined),
            p: 0,
          },
          children: value ?? "",
        });
      },
    })),
    // Add TOTAL column if measure supports it
    ...(shouldShowTotals
      ? [
          {
            key: "TOTAL",
            width: 120,
            sortable: true,
            onSort: handleTotalColumnSort,
            headerType: new arrow.Utf8(),
            renderCell: (row: RowData, rowIndex: number) => {
              const isGroup = row.type === "group";
              const isDetail = row.type === "detail";
              let total = 0;

              if (isGroup) {
                total = segmentationTotals.get(row.groupKey!) ?? 0;
              } else if (isDetail && row.groupKey && row.measureRank != null) {
                const measureTotalMap = measureTotals.get(row.groupKey);
                total = measureTotalMap?.get(row.measureRank) ?? 0;
              }

              const formattedTotal = total.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              });

              return React.createElement(Box, {
                sx: {
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  backgroundColor: isDetail ? "action.hover" : undefined,
                  p: 0,
                  fontWeight: isGroup ? "bold" : "normal",
                },
                children: formattedTotal,
              });
            },
          } as const,
        ]
      : []),
  ];

  // Lazy row access function - only convert to JS when requested
  const getRow = (rowIndex: number): RowData => {
    if (rowIndex >= totalRowCount)
      return { type: "detail", dateValues: {}, segmentationRank: 0 };

    // Find which segmentation this row belongs to
    let segmentationInfo: SegmentationInfo | null = null;
    let localRowIndex = 0;

    for (const info of segmentationInfos) {
      const segmentationEndIndex = info.startRowIndex + 1 + info.measureCount;
      if (rowIndex >= info.startRowIndex && rowIndex < segmentationEndIndex) {
        segmentationInfo = info;
        localRowIndex = rowIndex - info.startRowIndex;
        break;
      }
    }

    if (!segmentationInfo)
      return { type: "detail", dateValues: {}, segmentationRank: 0 };

    const segData = segmentationIndex.get(segmentationInfo.segmentationKey)!;

    // Row 0 is always the segmentation row
    if (localRowIndex === 0) {
      // Convert Maps to Record only when requested
      const dateValues: Record<string, any> = {};
      segData.selectedMeasureData.forEach((value, dateValue) => {
        dateValues[dateValue] = value;
      });

      const rowData: RowData = {
        type: "group" as const,
        groupKey: segmentationInfo.segmentationKey,
        dateValues,
        segmentationRank: segmentationInfo.segmentationRank,
        isExpanded:
          expandedGroups.has("*") ||
          expandedGroups.has(segmentationInfo.segmentationKey),
        groupSize: segData.allMeasures.size,
      };

      // Add export compatibility properties - direct column access
      rowData.segmentation = segmentationInfo.segmentationKey;
      allDates.forEach((date) => {
        const dateKey = `${date.valueOf()}`;
        rowData[dateKey] = dateValues[date.valueOf()] || "";
      });

      // Add TOTAL if applicable
      if (shouldShowTotals) {
        rowData["TOTAL"] =
          segmentationTotals.get(segmentationInfo.segmentationKey) ?? 0;
      }

      return rowData;
    }

    // Otherwise it's a measure row
    const measureIndex = localRowIndex - 1;

    // Sort measures by rank to get consistent ordering
    const sortedMeasures = Array.from(segData.allMeasures.values()).sort(
      (a, b) => a.measureRank - b.measureRank
    );

    if (measureIndex >= sortedMeasures.length)
      return { type: "detail", dateValues: {}, segmentationRank: 0 };
    const targetMeasure = sortedMeasures[measureIndex];

    // Convert Maps to Record only when requested
    const dateValues: Record<string, any> = {};
    targetMeasure.dateValues.forEach((value, dateValue) => {
      dateValues[dateValue] = value;
    });

    const rowData: RowData = {
      type: "detail" as const,
      groupKey: segmentationInfo.segmentationKey,
      measure: targetMeasure.measure,
      dateValues,
      segmentationRank: segmentationInfo.segmentationRank,
      measureRank: targetMeasure.measureRank,
      parentSegmentation: segmentationInfo.segmentationKey,
    };

    // Add export compatibility properties - direct column access
    rowData.segmentation = targetMeasure.measure; // Changed from "" to targetMeasure.measure
    rowData.measure = targetMeasure.measure;
    allDates.forEach((date) => {
      const dateKey = `${date.valueOf()}`;
      rowData[dateKey] = dateValues[date.valueOf()] || "";
    });

    // Add TOTAL if applicable
    if (shouldShowTotals && segmentationInfo) {
      const measureTotalMap = measureTotals.get(
        segmentationInfo.segmentationKey
      );
      rowData["TOTAL"] = measureTotalMap?.get(targetMeasure.measureRank) ?? 0;
    }

    return rowData;
  };

  // We don't actually need an Arrow table for virtualization - TableVirtuoso only uses getRow()
  // Return a minimal empty table just to satisfy the interface
  const emptyArrowTable = arrow.tableFromArrays({
    rowCount: [totalRowCount],
  });

  // Calculate column totals for the total row display
  let columnTotals: Record<string, number> | undefined = undefined;
  if (shouldShowTotals) {
    columnTotals = { TOTAL: 0 };
    // Sum up all totals from group rows (segmentation rows)
    segmentationIndex.forEach((segData) => {
      const total = segmentationTotals.get(segData.segmentationKey) ?? 0;
      columnTotals!["TOTAL"] += total;
    });
  }

  return {
    columns: muiColumns,
    getRow,
    rowCount: totalRowCount,
    arrowTable: emptyArrowTable,
    columnTotals,
    segmentationIndex,
  };
};
